import {
  Icon,
  SelectedTabProvider,
  Tab,
  Tabs,
} from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import React, { ReactNode, useEffect, useState } from 'react'
import { FulfilmentType } from '../../../models/fulfilment'
import { useAppSelector } from '../../../state/hooks'
import {
  selectCalculateTaxError,
  selectIsEcomOrder,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { useSaleHasServiceOrder } from '../../sale/useSaleHasServiceOrder'
import { LineItemTab } from '../../../../types/lineItemTabs'
import { useHasOutdatedTaxCategory } from './fulfilment/hook/useHasOutdatedTaxCategory'
import { useLineItemHasFulfillmentAddressError } from './fulfilment/hook/useLineItemHasFulfillmentAddressError'

interface LineItemTabsProps {
  defaultTab?: LineItemTab
  onChange: (selected: LineItemTab) => void
  children?: ReactNode | undefined
  fulfillmentType?: FulfilmentType
  lineItemId?: string
}

export const LineItemTabs: React.FC<LineItemTabsProps> = ({
  defaultTab = LineItemTab.DETAIL,
  onChange,
  children,
  fulfillmentType,
  lineItemId,
}) => {
  const t = useT()
  const [currentTab, setCurrentTab] = useState(defaultTab)
  const isEcomOrder = useAppSelector(selectIsEcomOrder)
  const saleHasServiceOrder = useSaleHasServiceOrder()
  const calculateTaxError = useAppSelector(selectCalculateTaxError)
  const hasAddressErrorForLineItemFulfillmentDelivery =
    (useLineItemHasFulfillmentAddressError() || !!calculateTaxError) &&
    fulfillmentType === FulfilmentType.DELIVERY
  const hasOutdatedTaxCategory =
    useHasOutdatedTaxCategory(lineItemId) &&
    fulfillmentType === FulfilmentType.DELIVERY

  const hasFulfillmentError =
    hasAddressErrorForLineItemFulfillmentDelivery || hasOutdatedTaxCategory

  const [displayErrorIcon, setDisplayErrorIcon] = useState(hasFulfillmentError)

  useEffect(() => {
    setDisplayErrorIcon(
      currentTab !== LineItemTab.FULFILMENT &&
        hasFulfillmentError &&
        !isEcomOrder
    )
  }, [currentTab, hasFulfillmentError, isEcomOrder])

  useEffect(() => {
    setCurrentTab(defaultTab)
  }, [defaultTab])

  const shouldShowTabs = !saleHasServiceOrder

  // Reset to Details tab when tabs are hidden (e.g., when service order is added while viewing Fulfillment tab)
  useEffect(() => {
    if (!shouldShowTabs && currentTab !== LineItemTab.DETAIL) {
      setCurrentTab(LineItemTab.DETAIL)
      onChange(LineItemTab.DETAIL)
    }
    // onChange is intentionally omitted from dependencies to avoid unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowTabs, currentTab])

  return (
    <SelectedTabProvider
      defaultTab={defaultTab}
      onChange={string => {
        const selectedTab = string as LineItemTab
        setCurrentTab(selectedTab)
        onChange(selectedTab)
      }}
    >
      {/* When tabs are hidden, thisBox provides the vd-mb3 margin that Tabs would normally provide */}
      <div className={shouldShowTabs ? undefined : 'vd-mb3'}>
        {shouldShowTabs && (
          <Tabs className="vd-mb3">
            <Tab data-track="details" name={LineItemTab.DETAIL}>
              {t('Details', { _context: 'Line item tabs' })}
            </Tab>
            <Tab data-track="fulfillment" name={LineItemTab.FULFILMENT}>
              {displayErrorIcon && (
                <span data-testid="line-item-icon-error" className="vd-mr2">
                  <Icon
                    icon="exclamation-triangle"
                    className="wr-input-error-icon"
                  />
                </span>
              )}
              {t('Fulfillment', { _context: 'Line item tabs' })}
            </Tab>
          </Tabs>
        )}
      </div>
      {children}
    </SelectedTabProvider>
  )
}
